import { useState, useEffect, useCallback } from 'react'
import { api } from '../../services/api.js'
import { btnPrimary, btnSecondary, btnDanger, colors, input, ConfirmDialog } from '../../components/ui/index.js'

interface Consent {
  readonly id: string
  readonly consentType: string
  readonly status: string
  readonly consentDate: string
  readonly capturedByName: string | null
  readonly note: string | null
}

interface ConsentPanelProps {
  readonly patientId: string
}

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.8125rem',
}
const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '0.375rem 0.5rem',
  borderBottom: `1px solid ${colors.border}`,
  color: colors.textMuted,
  fontWeight: 500,
  fontSize: '0.75rem',
}
const tdStyle: React.CSSProperties = {
  padding: '0.375rem 0.5rem',
  borderBottom: `1px solid ${colors.border}`,
}

export function ConsentPanel({ patientId }: ConsentPanelProps) {
  const [consents, setConsents] = useState<Consent[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Add form state
  const [showAdd, setShowAdd] = useState(false)
  const [addType, setAddType] = useState('treatment')
  const [addStatus, setAddStatus] = useState('obtained')
  const [addNote, setAddNote] = useState('')
  const [saving, setSaving] = useState(false)

  // Revoke confirm state
  const [revokeId, setRevokeId] = useState<string | null>(null)
  const [revoking, setRevoking] = useState(false)

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    api
      .get<Consent[]>(`/patients/${patientId}/consents`)
      .then((res) => {
        setConsents(res.data ?? [])
        setLoading(false)
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to load consents')
        setLoading(false)
      })
  }, [patientId])

  useEffect(() => {
    load()
  }, [load])

  async function handleAdd() {
    setSaving(true)
    setError(null)
    try {
      await api.post(`/patients/${patientId}/consents`, {
        consentType: addType,
        status: addStatus,
        note: addNote || undefined,
      })
      setShowAdd(false)
      setAddType('treatment')
      setAddStatus('obtained')
      setAddNote('')
      load()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to add consent')
    } finally {
      setSaving(false)
    }
  }

  async function handleRevoke() {
    if (!revokeId) return
    setRevoking(true)
    try {
      await api.delete(`/patients/${patientId}/consents/${revokeId}`)
      setRevokeId(null)
      load()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to revoke consent')
    } finally {
      setRevoking(false)
    }
  }

  return (
    <>
      <div
        style={{
          gridColumn: '1 / -1',
          borderTop: `1px solid ${colors.border}`,
          paddingTop: '0.75rem',
          marginTop: '0.75rem',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
          <h3 style={{ margin: 0, fontSize: '0.9375rem', fontWeight: 600, color: colors.text }}>HIPAA Consents</h3>
          {!showAdd && (
            <button type="button" onClick={() => setShowAdd(true)} style={btnSecondary}>
              + Add Consent
            </button>
          )}
        </div>

        {error && (
          <div
            style={{
              marginBottom: '0.5rem',
              color: colors.danger,
              padding: '0.375rem 0.5rem',
              background: colors.bgError,
              border: `1px solid ${colors.borderError}`,
              borderRadius: '3px',
              fontSize: '0.8125rem',
            }}
          >
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ color: colors.textMuted, fontSize: '0.8125rem' }}>Loading...</div>
        ) : (
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Captured By</th>
                <th style={thStyle}>Note</th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {consents.length === 0 && !showAdd && (
                <tr>
                  <td colSpan={6} style={{ ...tdStyle, color: colors.textMuted, fontStyle: 'italic' }}>
                    No consents
                  </td>
                </tr>
              )}
              {consents.map((c) => (
                <tr key={c.id}>
                  <td style={tdStyle}>{c.consentType}</td>
                  <td style={tdStyle}>{c.status}</td>
                  <td style={tdStyle}>{c.consentDate ? c.consentDate.slice(0, 10) : ''}</td>
                  <td style={tdStyle}>{c.capturedByName ?? ''}</td>
                  <td style={tdStyle}>{c.note ?? ''}</td>
                  <td style={tdStyle}>
                    <button
                      type="button"
                      onClick={() => setRevokeId(c.id)}
                      style={{ ...btnDanger, padding: '0.125rem 0.5rem', fontSize: '0.75rem' }}
                    >
                      Revoke
                    </button>
                  </td>
                </tr>
              ))}
              {showAdd && (
                <tr>
                  <td style={tdStyle}>
                    <select
                      style={{ ...input, fontSize: '0.8125rem' }}
                      value={addType}
                      onChange={(e) => setAddType(e.target.value)}
                    >
                      <option value="treatment">treatment</option>
                      <option value="payment">payment</option>
                      <option value="operations">operations</option>
                      <option value="research">research</option>
                      <option value="marketing">marketing</option>
                    </select>
                  </td>
                  <td style={tdStyle}>
                    <select
                      style={{ ...input, fontSize: '0.8125rem' }}
                      value={addStatus}
                      onChange={(e) => setAddStatus(e.target.value)}
                    >
                      <option value="obtained">obtained</option>
                      <option value="declined">declined</option>
                    </select>
                  </td>
                  <td style={tdStyle} />
                  <td style={tdStyle} />
                  <td style={tdStyle}>
                    <input
                      style={{ ...input, fontSize: '0.8125rem' }}
                      placeholder="Note (optional)"
                      value={addNote}
                      onChange={(e) => setAddNote(e.target.value)}
                    />
                  </td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', gap: '0.25rem' }}>
                      <button
                        type="button"
                        onClick={handleAdd}
                        style={{ ...btnPrimary, padding: '0.125rem 0.5rem', fontSize: '0.75rem' }}
                        disabled={saving}
                      >
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowAdd(false)}
                        style={{ ...btnSecondary, padding: '0.125rem 0.5rem', fontSize: '0.75rem' }}
                        disabled={saving}
                      >
                        Cancel
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <ConfirmDialog
        isOpen={revokeId !== null}
        onClose={() => setRevokeId(null)}
        onConfirm={handleRevoke}
        title="Revoke Consent"
        message="Are you sure you want to revoke this consent? This action cannot be undone."
        confirmLabel="Revoke"
        loading={revoking}
      />
    </>
  )
}
